import React from "react";
import { Calendar as CalIcon } from "lucide-react";
import { useLang } from "@/lib/lang";

// Iter 23 Sub-Fase A: presets today | day (data especifica) | week | month | year | custom
const PRESETS = ["today", "day", "week", "month", "year", "custom"];

const isoDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * DateFilter — reusable time-range filter.
 * Emits { preset, from, to } where from/to are ISO dates (YYYY-MM-DD).
 * Preset "day" needs a "day_value" to pick a specific day.
 */
export default function DateFilter({ value, onChange, testId = "date-filter" }) {
  const { t } = useLang();
  const preset = value?.preset || "week";
  const from = value?.from || "";
  const to = value?.to || "";
  const dayValue = value?.day_value || isoDate(new Date());

  const applyPreset = (p, dayISO) => {
    const now = new Date();
    if (p === "today") {
      const d = isoDate(now);
      onChange({ preset: p, from: d, to: d });
      return;
    }
    if (p === "day") {
      const target = dayISO || dayValue;
      onChange({ preset: p, from: target, to: target, day_value: target });
      return;
    }
    if (p === "week") {
      // current ISO week (mon → sun)
      const d = new Date(now);
      const day = (d.getDay() + 6) % 7; // 0 = mon
      const mon = new Date(d); mon.setDate(d.getDate() - day);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      onChange({ preset: p, from: isoDate(mon), to: isoDate(sun) });
      return;
    }
    if (p === "month") {
      const y = now.getFullYear(); const m = now.getMonth();
      const first = new Date(y, m, 1);
      const last = new Date(y, m + 1, 0);
      onChange({ preset: p, from: isoDate(first), to: isoDate(last) });
      return;
    }
    if (p === "year") {
      const y = now.getFullYear();
      onChange({ preset: p, from: `${y}-01-01`, to: `${y}-12-31` });
      return;
    }
    if (p === "custom") {
      onChange({ preset: "custom", from, to });
      return;
    }
  };

  React.useEffect(() => {
    if (!value?.from || !value?.to) applyPreset(preset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid={testId}>
      <div className="flex items-center gap-1 border border-slate-300">
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            data-testid={`${testId}-${p}`}
            onClick={() => applyPreset(p)}
            className={`h-9 px-3 text-[11px] uppercase tracking-widest font-semibold transition-colors ${
              preset === p ? "bg-[#0a0f1e] text-white" : "text-slate-700 hover:bg-slate-100"
            }`}
          >
            {t(`view_${p}`)}
          </button>
        ))}
      </div>
      {preset === "day" && (
        <label className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-slate-700 font-semibold">
          <CalIcon className="w-3.5 h-3.5" /> {t("view_day")}
          <input
            type="date"
            value={dayValue}
            onChange={(e) => applyPreset("day", e.target.value)}
            className="ml-1 h-8 border border-slate-300 px-2 text-xs"
            data-testid={`${testId}-day-value`}
          />
        </label>
      )}
      {preset === "custom" && (
        <>
          <label className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-slate-700 font-semibold">
            <CalIcon className="w-3.5 h-3.5" /> {t("from_date")}
            <input
              type="date"
              value={from}
              onChange={(e) => onChange({ ...value, from: e.target.value })}
              className="ml-1 h-8 border border-slate-300 px-2 text-xs"
              data-testid={`${testId}-from`}
            />
          </label>
          <label className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-slate-700 font-semibold">
            {t("to_date")}
            <input
              type="date"
              value={to}
              onChange={(e) => onChange({ ...value, to: e.target.value })}
              className="ml-1 h-8 border border-slate-300 px-2 text-xs"
              data-testid={`${testId}-to`}
            />
          </label>
        </>
      )}
    </div>
  );
}
